/** Currency with an explicit sign for P&L figures: +$1,234.56 / -$1,234.56 / $0.00. */
export const formatSignedCurrency = (value: number | null | undefined, fractionDigits = 2): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';

    const magnitude = Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    });

    if (value > 0) return `+$${magnitude}`;
    if (value < 0) return `-$${magnitude}`;
    return `$${magnitude}`;
};

/** Currency without a sign, for balances and sizes. */
export const formatUnsignedCurrency = (value: number | null | undefined, fractionDigits = 2): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';

    return `$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })}`;
};

/** Percent with an explicit sign: +1.23% / -1.23% / 0.00%. */
export const formatSignedPercent = (value: number | null | undefined, fractionDigits = 2): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';

    const magnitude = Math.abs(value).toFixed(fractionDigits);

    if (value > 0) return `+${magnitude}%`;
    if (value < 0) return `-${magnitude}%`;
    return `${magnitude}%`;
};

/** Percent without a sign, for rates such as win rate. */
export const formatRatePercent = (value: number | null | undefined, fractionDigits = 1): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    return `${value.toFixed(fractionDigits)}%`;
};

export const formatRatio = (value: number | null | undefined, fractionDigits = 2): string => {
    if (value === null || value === undefined) return '—';
    if (!Number.isFinite(value)) return '∞';
    return value.toFixed(fractionDigits);
};

export const formatDays = (value: number | null | undefined, fractionDigits = 1): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    return `${value.toFixed(fractionDigits)}d`;
};

/** Tailwind text color for a signed figure. */
export const signedToneClass = (value: number | null | undefined, zeroClass = 'text-zinc-400'): string => {
    if (value === null || value === undefined || !Number.isFinite(value) || value === 0) return zeroClass;
    return value > 0 ? 'text-profit-green' : 'text-red-500';
};

/** Tailwind text color for a rate compared to a threshold (win rate vs 50%, profit factor vs 1). */
export const thresholdToneClass = (value: number | null | undefined, threshold: number, zeroClass = 'text-zinc-400'): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return zeroClass;
    if (value === threshold) return 'text-zinc-200';
    return value > threshold ? 'text-profit-green' : 'text-red-500';
};
